import React, { useState, useEffect, useCallback } from "react";
import { useSiteData } from "../../context/SiteDataContext";

const HeroSection = () => {
  const { presale, siteConfig } = useSiteData();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  const [payMethod, setPayMethod] = useState("USDT");
  const [amount, setAmount] = useState("");
  const [tokensReceived, setTokensReceived] = useState("0");

  const logoUrl = siteConfig?.logoUrl || "/marsu.png";
  const price = presale?.price || 0.000055;
  const endDate = presale?.endDate;
  const isLive = presale?.isLive !== false;
  const raised = presale?.raised || 0;
  const target = presale?.target || 5000000;
  const paymentMethods = presale?.paymentMethods || ["USDT", "USDC"];

  const calculateTimeLeft = useCallback(() => {
    if (!endDate) return { days: 0, hours: 0, mins: 0, secs: 0 };
    const end = new Date(endDate).getTime();
    const now = Date.now();
    const diff = end - now;
    if (diff <= 0) return { days: 0, hours: 0, mins: 0, secs: 0 };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      mins: Math.floor((diff / (1000 * 60)) % 60),
      secs: Math.floor((diff / 1000) % 60),
    };
  }, [endDate]);

  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  useEffect(() => {
    if (amount && !isNaN(amount) && Number(amount) > 0) {
      const tokens = Number(amount) / price;
      setTokensReceived(Math.floor(tokens).toLocaleString());
    } else {
      setTokensReceived("0");
    }
  }, [amount, price]);

  const pad = (n) => String(n).padStart(2, "0");
  const progressPercent = target > 0 ? (raised / target) * 100 : 0;

  return (
    <section className="relative min-h-screen pt-20 overflow-hidden" id="hero">
      <div className="absolute inset-0 bg-[#060d06] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-950/40 via-[#060d06] to-yellow-950/20" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-green-600/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-600/5 rounded-full blur-[120px]" />
        
        {/* Jungle Leaves - Background Decoration */}
        <div className="absolute top-[10%] left-[5%] text-green-800/20 animate-leaf-float leaf-delay-1">
          <svg width="120" height="120" viewBox="0 0 100 100" fill="currentColor">
            <path d="M 50,10 C 70,30 90,50 50,90 C 10,50 30,30 50,10 Z" />
          </svg>
        </div>
        <div className="absolute top-[60%] right-[10%] text-green-900/10 animate-leaf-float leaf-delay-2 rotate-45 scale-150">
          <svg width="100" height="100" viewBox="0 0 100 100" fill="currentColor">
            <path d="M 50,10 C 70,30 90,50 50,90 C 10,50 30,30 50,10 Z" />
          </svg>
        </div>
        <div className="absolute top-[20%] right-[30%] text-green-700/15 animate-leaf-float leaf-delay-3 -rotate-12">
          <svg width="80" height="80" viewBox="0 0 100 100" fill="currentColor">
            <path d="M 50,10 C 70,30 90,50 50,90 C 10,50 30,30 50,10 Z" />
          </svg>
        </div>
        <div className="absolute bottom-[20%] left-[20%] text-green-900/10 animate-leaf-float leaf-delay-4 rotate-90 scale-125">
          <svg width="150" height="150" viewBox="0 0 100 100" fill="currentColor">
            <path d="M 50,10 C 70,30 90,50 50,90 C 10,50 30,30 50,10 Z" />
          </svg>
        </div>

        <div className="absolute top-20 right-[20%] w-3 h-3 bg-yellow-400/30 rounded-full animate-pulse" />
        <div className="absolute top-40 left-[10%] w-2 h-2 bg-green-400/40 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-40 left-[30%] w-2 h-2 bg-yellow-400/20 rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Giant Hanging Marsupilami - Wrapper for horizontal centering & desktop offset */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 lg:left-[32%] lg:-translate-x-1/2 w-[1000px] sm:w-[1200px] lg:w-[1300px] pointer-events-none z-10">
        {/* Static Background Glow - This does NOT drop with the character */}
        <div className="absolute top-[35%] sm:top-[40%] left-1/2 -translate-x-1/2 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-yellow-400/10 rounded-full blur-[100px] animate-pulse pointer-events-none" />
        
        {/* Wrapper for vertical drop animation */}
        <div className="marsupilami-drop w-full h-full relative">
          <img 
            src="/marsupilami-hanging.png" 
            alt="Marsupilami Hanging" 
            className="w-full h-auto object-contain marsupilami-swing drop-shadow-[0_0_120px_rgba(255,215,0,0.5)]" 
            style={{ 
              mixBlendMode: 'multiply',
              filter: 'contrast(1.1) brightness(1.1)',
              zIndex: 1,
              transformOrigin: 'top center'
            }}
          />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-8 py-12 lg:py-20">
        <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left relative min-h-[500px] sm:min-h-[600px] lg:min-h-0">
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left pt-[350px] sm:pt-[450px] lg:pt-[450px] relative z-20">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold mt-12 leading-tight whitespace-nowrap">
              <span className="text-yellow-400">{siteConfig?.coinName || 'MARSUPILAMI'}</span>{" "}
              <span className="text-white">COIN</span>
            </h1>
            <p className="text-gray-400 text-lg sm:text-2xl mt-4 max-w-md">
              {siteConfig?.tagline || 'Huba Huba! Conquer The Crypto World With The King of the Jungle!'}
            </p>
          </div>
        </div>

        <div className="w-full max-w-md lg:max-w-lg relative z-20">
          <div className="bg-[#0d1a0d]/90 backdrop-blur-sm border border-[#1a3a1a] rounded-2xl p-6 shadow-2xl shadow-black/40">
            {isLive && (
              <div className="flex justify-center mb-4">
                <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/40 rounded-full px-5 py-1.5">
                  <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-green-400 font-bold text-sm tracking-wider">LIVE</span>
                </div>
              </div>
            )}

            <div className="border border-[#1a3a1a] rounded-xl p-4 mb-4 text-center bg-[#060d06]/50">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">MRS Price:</p>
              <p className="text-yellow-400 text-3xl font-bold">${price}</p>
            </div>


            <div className="w-full bg-[#1a3a1a] rounded-full h-2.5 mb-4">
              <div className="h-2.5 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-400 transition-all duration-500" style={{ width: `${Math.min(progressPercent, 100)}%` }} />
            </div>
            <p className="text-center text-xs text-gray-500 mb-4">${raised.toLocaleString()} / ${target.toLocaleString()} USDT</p>

            <div className="grid grid-cols-4 gap-3 mb-5">
              {[
                { val: timeLeft.days, label: "DAYS" },
                { val: timeLeft.hours, label: "HOURS" },
                { val: timeLeft.mins, label: "MINS" },
                { val: timeLeft.secs, label: "SECS" },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <div className="text-yellow-400 text-2xl sm:text-3xl font-bold font-mono">{pad(item.val)}</div>
                  <div className="text-gray-500 text-[10px] uppercase tracking-wider mt-1">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="mb-4">
              <p className="text-gray-400 text-sm mb-2">Pay with:</p>
              <div className="flex gap-2">
                {paymentMethods.map((method) => (
                  <button key={method} onClick={() => setPayMethod(method)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${payMethod === method ? "bg-yellow-400/20 border border-yellow-400 text-yellow-400" : "bg-[#1a3a1a] border border-[#2a4a2a] text-gray-400 hover:border-yellow-400/50"}`}>{method}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div>
                <label className="text-gray-400 text-xs mb-1 block">{payMethod} Amount</label>
                <input type="number" placeholder="10" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white placeholder-gray-600 focus:border-yellow-400/50 focus:outline-none transition-colors text-sm" />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">You will receive (MRS)</label>
                <input type="text" readOnly value={tokensReceived} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm cursor-default" />
              </div>
            </div>

            <button className="w-full py-3.5 rounded-xl font-bold text-black text-sm uppercase tracking-wider bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 transition-all duration-300 shadow-lg shadow-yellow-400/20 hover:shadow-yellow-400/40 hover:scale-[1.02] active:scale-[0.98]">CONNECT WALLET</button>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-[#1a3a1a]" />
              <span className="text-gray-500 text-xs">- OR -</span>
              <div className="flex-1 h-px bg-[#1a3a1a]" />
            </div>

            <button className="w-full text-center text-yellow-400 hover:text-yellow-300 text-sm font-medium transition-colors">MANUAL BUY</button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
